/**
 * Field-level diff of the `before` / `after` JSON of an `audit_log` row (docs/06 §2.13,
 * Figma CMS-14): one line per key that differs, `undefined` on the side where it is absent.
 */
export interface DiffField {
  field: string;
  before: unknown;
  after: unknown;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

const same = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/**
 * Both sides objects → the union of their keys, changed ones only. Anything else (a scalar,
 * an array, a create with only `after`) → a single `value` row, so nothing is ever hidden.
 */
export function diffFields(before: unknown, after: unknown): DiffField[] {
  const b = asRecord(before);
  const a = asRecord(after);
  if (!b && !a) {
    // Both sides absent (a publish, a close…) records no field change at all.
    return before == null && after == null ? [] : [{ field: 'value', before, after }];
  }
  if (!b || !a) {
    const present = (b ?? a) as Record<string, unknown>;
    return Object.keys(present).map((field) => ({
      field,
      before: b ? b[field] : undefined,
      after: a ? a[field] : undefined,
    }));
  }
  const keys = [...new Set([...Object.keys(b), ...Object.keys(a)])].sort();
  return keys
    .filter((k) => !same(b[k], a[k]))
    .map((field) => ({
      field,
      before: b[field],
      after: a[field],
    }));
}
